import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { selectors } from "reducers";
import { fetchPatientChangeLogs } from "actions/patient";
import useLocalQuery, { Table } from "hooks/useQuery/local-query";
import { get } from "utils/api";
import { useFetch } from "hooks";
import PureModal from "components/ui/modal/pure-modal";
import { InlineWaiting } from "components/ui";
import { formatDate } from "utils/dates";

const ChangeLogsTab = ({
  patientId,
  fetchPatientChangeLogs,
  changeLogs,
  isFetchingChangeLogs,
  patientInfo
}) => {
  const updatedPatientInfo = useMemo(() => {
    return { patientInfo };
  }, [patientInfo]);

  const { response, isFetching, fetchData } = useFetch({
    apiFn: patientId => get(`patients/${patientId}/logs/import`),
    defaultValue: { logs: [] }
  });

  useEffect(() => {
    fetchPatientChangeLogs({ patientId });
    fetchData(patientId);
  }, [updatedPatientInfo]);

  const { rows, query, queryDispatcher } = useLocalQuery(changeLogs, {
    sort: { by: "changedDate", direction: "desc" }
  });
  return (
    <div>
      <Table
        isFetching={isFetchingChangeLogs}
        containerClass="query-table patient-tab-table dynamic-columns"
        TableRow={ChangeLogsTableRow}
        columns={[
          {
            header: "Time",
            field: "changedDate",
            style: { width: 200 }
          },
          {
            header: "User",
            field: "changedByName",
            style: { width: 200 }
          },
          {
            header: "Changes",
            field: "changedField"
          }
        ]}
        query={query}
        queryDispatcher={queryDispatcher}
        rows={rows}
        rowKey="id"
        renderFooter={
          <PureModal
            renderTrigger={({ openModal }) =>
              isFetching ? (
                <InlineWaiting />
              ) : (
                <button style={{ margin: 0, padding: 5 }} onClick={openModal}>
                  Import Logs
                </button>
              )
            }
          >
            <table>
              <thead>
                <tr>
                  <td>Date</td>
                  <td>File</td>
                  <td>Created By</td>
                  <td>Status</td>
                  <td>Error</td>
                </tr>
              </thead>
              <tbody>
                {response?.logs.map(x => (
                  <tr key={x.id}>
                    <td>{formatDate(x.timestamp, "MMM DD, YYYY hh:mm A")}</td>
                    <td>{x.file}</td>
                    <td>{x.created_by}</td>
                    <td>{x.status}</td>
                    <td>{x.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </PureModal>
        }
      />
    </div>
  );
};

export default connect(
  (state, { patientId }) => {
    const isFetchingChangeLogs = state.getIn(
      ["fetch", fetchPatientChangeLogs.baseType, patientId, "isFetching"],
      false
    );
    return {
      changeLogs: selectors.getPatientChangeLogs(state, patientId),
      isFetchingChangeLogs
    };
  },
  { fetchPatientChangeLogs }
)(ChangeLogsTab);

ChangeLogsTab.propTypes = {
  patientId: PropTypes.string.isRequired,
  fetchPatientChangeLogs: PropTypes.func.isRequired,
  changeLogs: PropTypes.array,
  isFetchingChangeLogs: PropTypes.bool,
  patientInfo: PropTypes.object
};

const ChangeLogsTableRow = ({
  changedDate,
  changedByName,
  changedField,
  oldValue,
  newValue
}) => (
  <tr>
    <td>{formatDate(changedDate, "MMM DD, YYYY hh:mm A")}</td>
    <td>{changedByName}</td>
    <td>
      {changedField} changed from {oldValue} to {newValue}
    </td>
  </tr>
);

ChangeLogsTableRow.propTypes = {
  changedDate: PropTypes.string,
  changedByName: PropTypes.string,
  changedField: PropTypes.string,
  oldValue: PropTypes.string,
  newValue: PropTypes.string
};
